import { StrictMode, Suspense, lazy, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom';
import App from './App';
import UnderConstruction from './pages/UnderConstruction';
import LegalPage from './pages/LegalPage';
import NotFound from './pages/NotFound';
import { LanguageProvider } from './i18n/LanguageProvider';
// Lazy so SheetJS (~800KB) stays in its own chunk and never touches the
// marketing bundle — it's only ever needed on this one operator page.
const AdminEventRank = lazy(() => import('./pages/AdminEventRank'));
import { useLanguage } from './i18n/useLanguage';
import type { Lang } from './i18n/messages';
import './styles/tokens.css';
import './styles/index.css';

// Shareable language entry-points: `/en` and `/th` set the language
// (persisted to localStorage by LanguageProvider) and redirect to `/`.
// They're deep-links, not separate indexable pages — there is still one
// canonical URL (`/`), so they're excluded from sitemap.xml. Setting the
// language AND navigating in a single effect (rather than rendering
// <Navigate>) avoids the unmount race where a redirect could fire before
// the language is applied.
function LangRedirect({ to }: { to: Lang }) {
  const { setLang } = useLanguage();
  const navigate = useNavigate();
  useEffect(() => {
    setLang(to);
    navigate('/', { replace: true });
  }, [to, setLang, navigate]);
  return null;
}

// Routing layout for launch:
//   • `/`            — the full marketing app (Header + every section
//                      + Footer).
//   • `/preview/*`   — the old under-construction landing kept as a
//                      fallback preview/support page.
//   • `/privacy`,    — minimal frames around the static legal
//     `/tos`           HTML in /public/legal; language follows
//                      the in-page switcher.
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<App />} />
          <Route path="/preview/*" element={<UnderConstruction />} />
          <Route path="/privacy" element={<LegalPage doc="privacy" />} />
          <Route path="/tos" element={<LegalPage doc="tos" />} />
          {/* Operator surface for publishing the event ranking. The
              public leaderboard is NOT here — it's a separate Vite
              entry at /inapp/event_rank.html, loaded by the game. */}
          <Route
            path="/admin/event_rank"
            element={
              <Suspense fallback={null}>
                <AdminEventRank />
              </Suspense>
            }
          />
          {/* Language entry-point redirects → set lang, then go to /. */}
          <Route path="/en" element={<LangRedirect to="en" />} />
          <Route path="/th" element={<LangRedirect to="th" />} />
          {/* Catch-all 404 → branded page that auto-redirects home. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  </StrictMode>,
);
